import { getSaludTour } from './tourConfigsSalud.js'
// Configuraciones de tours guiados para diferentes perfiles de usuario

// Tour del administrador / supervisor.
// Pensado como un RECORRIDO SERIAL DE PUESTA EN MARCHA con la plataforma VACÍA:
// enseña, en el orden lógico en que un usuario nuevo debe configurar el sistema,
// cada funcionalidad (incluyendo todo lo agregado recientemente: onboarding de 2
// ejes, 5 roles granulares, recepción con cuarentena, estrategia FIFO/FEFO
// heredada del producto, guía de despacho PDF, etc.).
//
// Nota de robustez: cada paso se ancla a un `data-tour` del menú lateral (siempre
// presente) y, para módulos que una edición Pyme podría ocultar, se agrega
// `, [data-tour="sidebar"]` como respaldo para que el tooltip nunca quede huérfano.
export const supervisorTour = [
  // 1. Bienvenida
  {
    target: '.main-content',
    title: '¡Bienvenido a Trazalogis WMS!',
    description: 'Te guiaremos para poner en marcha tu bodega desde cero, en el orden correcto: primero defines cómo opera tu negocio, luego tu equipo, tus bodegas y tus productos, y al final los flujos de entrada (recepción) y salida (despacho). Cada paso explica una funcionalidad. ¡Comencemos!',
  },

  // 2. Configuración inicial — los 2 ejes (onboarding)
  {
    target: '[data-tour="sidebar"]',
    title: 'Paso 0 · Configura tu operación',
    description: 'La primera vez, el sistema te hace 2 preguntas: "¿Manejas precios?" y "¿Tienes operarios de bodega?". Tus respuestas adaptan Trazalogis a tu realidad (modo Pyme o Empresa) y deciden qué módulos aparecen en el menú. Puedes cambiarlo cuando quieras con los interruptores que verás en este panel.',
  },

  // 3. Panel de navegación
  {
    target: '[data-tour="sidebar"]',
    title: 'Tu panel de navegación',
    description: 'Desde aquí accedes a todo. El menú se adapta automáticamente a tu edición (Pyme/Empresa) y a tu rol: cada usuario ve solo las secciones que le corresponden. Iremos visitando cada una en orden.',
  },

  // 4. Usuarios y roles granulares
  {
    target: '[data-tour="users-link"]',
    title: 'Paso 1 · Crea tu equipo y sus roles',
    description: 'Empieza por las personas. Trazalogis tiene 5 roles con permisos estrictos: Administrador, Jefe de Bodega, Operario de Recepción, Picker (recolector) y Vendedor. Cada rol ve solo lo suyo; por ejemplo, un operario o picker NUNCA ve costos ni precios. Usa el botón "Nuevo Usuario" para sumar a tu equipo.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/users') {
        router.push('/users')
      }
    }
  },

  // 5. Instalaciones: Bodegas → Zonas → Ubicaciones
  {
    target: '[data-tour="installations-link"]',
    title: 'Paso 2 · Arma tu bodega física',
    description: 'Define el mapa donde vivirá tu stock. La jerarquía es: Bodega → dentro de ella Zonas (ej. Refrigerados, Zona A) → dentro de cada zona Ubicaciones (rack y posición). Crea primero una Bodega, luego sus Zonas y Ubicaciones. Sin esta estructura no se puede guardar inventario.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/instalaciones') {
        router.push('/instalaciones')
      }
    }
  },

  // 6. Productos: la ficha (sin stock)
  {
    target: '[data-tour="inventory-link"]',
    title: 'Paso 3 · Crea las fichas de tus productos',
    description: 'Aquí defines QUÉ vendes, no CUÁNTO tienes: el stock se carga al recibir, no al crear el producto. En la ficha verás 3 códigos (SKU = tu código interno, Código de Barras/GTIN = el comercial, Código de Fábrica = el del proveedor), la estrategia de salida FIFO o FEFO, el control de vencimiento y hasta una foto del producto para que el picker lo identifique. La categoría es opcional.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/inventario') {
        router.push('/inventario')
      }
    }
  },

  // 7. Ingresos — recepción simplificada
  {
    target: '[data-tour="ingresos-link"]',
    title: 'Paso 4a · Recepción simplificada (cargar stock)',
    description: 'La forma directa de ingresar mercancía: registras el producto con su lote, número de serie y vencimiento, y queda disponible en inventario al instante. Si escaneas un código GS1/DataMatrix, el sistema separa automáticamente la fecha de fabricación y la de vencimiento, y reconoce el producto por su GTIN.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/ingresos') {
        router.push('/ingresos')
      }
    }
  },

  // 8. Recepciones con cuarentena (NUEVO)
  {
    target: '[data-tour="recepciones-link"]',
    title: 'Paso 4b · Recepción con cuarentena (control de diferencias)',
    description: 'El flujo profesional de recepción. El Jefe carga lo ESPERADO (a mano o extrayéndolo de un PDF de guía/factura). El operario cuenta a CIEGAS —sin ver la cantidad esperada, para evitar sesgos—. El sistema compara: lo que calza entra al inventario y el sobrante queda en CUARENTENA hasta que el Jefe lo apruebe. Soporta entregas parciales contra una orden de compra.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/recepciones') {
        router.push('/recepciones')
      }
    }
  },

  // 9. Egresos — salida simple
  {
    target: '[data-tour="egresos-link"], [data-tour="sidebar"]',
    title: 'Paso 5 · Salidas — Egresos',
    description: 'La manera simple de sacar stock, asociada al RUT del cliente. La estrategia FIFO/FEFO ya NO se elige a mano: se hereda automáticamente de cada producto, para no despachar nunca el lote equivocado (clave en productos con vencimiento). Si tu operación maneja precios, aquí verás el precio unitario y el total valorizado.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/egresos') {
        router.push('/egresos')
      }
    }
  },

  // 10. Gestión de Órdenes (avanzado)
  {
    target: '[data-tour="orders-link"], [data-tour="sidebar"]',
    title: 'Paso 5 (avanzado) · Gestión de Órdenes',
    description: 'Si trabajas con precios Y con operarios, este es tu centro de salidas: creas pedidos de venta, los asignas y haces seguimiento de principio a fin (cliente, productos, cantidades y estado del pedido). Alimenta directamente las tareas de picking.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/orders') {
        router.push('/orders')
      }
    }
  },

  // 11. Picking Inteligente + guía de despacho PDF (NUEVO)
  {
    target: '[data-tour="intelligent-picking-link"], [data-tour="sidebar"]',
    title: 'Paso 6 · Picking Inteligente y guía de despacho',
    description: 'Genera tareas de recolección con la ruta óptima por la bodega. El picker las ejecuta desde su teléfono escaneando cada producto (sin ver precios). Al completar el picking, el sistema genera automáticamente la GUÍA DE DESPACHO en PDF: un documento operativo (no tributario, sin precios) con destinatario, lote, vencimiento, ubicación y cantidades.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/intelligent-picking') {
        router.push('/intelligent-picking')
      }
    }
  },

  // 12. Asignación de Servicios
  {
    target: '[data-tour="service-assignment-link"], [data-tour="sidebar"]',
    title: 'Paso 7 · Asignación de Servicios',
    description: 'El Jefe de Bodega reparte el trabajo: asigna cada lista de picking (y las tareas de almacenamiento) a un operario disponible. Así cada picker ve solo las tareas que le tocan.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/service-assignment') {
        router.push('/service-assignment')
      }
    }
  },

  // 13. Despachos
  {
    target: '[data-tour="despachos-link"], [data-tour="sidebar"]',
    title: 'Paso 8 · Despachos',
    description: 'El flujo avanzado de envíos: consolidación de pedidos, gestión de muelles de carga y documentación de los despachos. Útil cuando manejas volúmenes altos de salida.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/despachos') {
        router.push('/despachos')
      }
    }
  },

  // 14. Movimientos Internos
  {
    target: '[data-tour="movimientos-internos-link"]',
    title: 'Paso 9 · Movimientos Internos',
    description: 'Reorganiza tu bodega sin que entre ni salga mercancía: transfiere o reubica stock de una ubicación a otra (origen → destino). Ideal para reacomodar zonas o consolidar lotes.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/movimientos-internos') {
        router.push('/movimientos-internos')
      }
    }
  },

  // 15. Trazabilidad
  {
    target: '[data-tour="trazabilidad-link"]',
    title: 'Paso 10 · Trazabilidad',
    description: 'El historial completo de cada producto: todos sus ingresos, movimientos y salidas quedan registrados con fecha, lote y responsable. Indispensable para auditorías y para productos médicos con códigos GS1/DataMatrix.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/trazabilidad') {
        router.push('/trazabilidad')
      }
    }
  },

  // 16. Gestión de Costos
  {
    target: '[data-tour="cost-management-link"], [data-tour="sidebar"]',
    title: 'Paso 11 · Gestión de Costos',
    description: 'Si manejas precios, aquí controlas los costos y precios de tus productos y analizas la rentabilidad y la clasificación ABC por ventas. (Esta sección se oculta automáticamente si tu operación no maneja precios.)',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/cost-management') {
        router.push('/cost-management')
      }
    }
  },

  // 17. Análisis / Analytics
  {
    target: '[data-tour="analytics-link"]',
    title: 'Paso 12 · Análisis y KPIs',
    description: 'Tu tablero de control: rotación ABC, productos bajo stock, punto de reorden y cumplimiento de pedidos. A medida que cargues datos reales, estos indicadores cobran vida y te ayudan a tomar decisiones.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/analytics') {
        router.push('/analytics')
      }
    }
  },

  // 18. Finalización
  {
    target: '.main-content',
    title: '¡Listo para operar!',
    description: 'Ese es el recorrido completo y el orden recomendado: 1) Equipo y roles → 2) Bodegas → 3) Productos → 4) Recepción (simple o con cuarentena) → 5/6) Salidas y picking → guía de despacho → trazabilidad y análisis. Puedes volver a abrir este tour cuando quieras desde el botón de ayuda. ¡A configurar tu bodega!',
  }
]

export const operatorTour = [
  // 1. Bienvenida
  {
    target: '.main-content',
    title: '¡Bienvenido Operario!',
    description: 'Te mostraremos cómo usar las herramientas principales para tus tareas diarias de picking y gestión de inventario.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/operator') {
        router.push('/operator')
      }
    }
  },

  // 2. Dashboard del Operario
  {
    target: '.operator-header',
    title: 'Tu Dashboard Personal',
    description: 'Aquí puedes ver tu información personal y el resumen de tus tareas asignadas.',
  },

  // 3. Tareas Asignadas
  {
    target: '.tasks-summary, [data-tour="tasks-summary"]',
    title: 'Resumen de Tareas',
    description: 'Visualiza cuántas tareas tienes pendientes, en progreso y completadas. Este es tu centro de control diario.',
  },

  // 4. Lista de Picking
  {
    target: '.picking-list-table, [data-tour="picking-list"]',
    title: 'Lista de Tareas de Picking',
    description: 'Aquí aparecen todas las tareas de recolección que te han sido asignadas. Puedes ver el estado y detalles de cada una.',
  },

  // 5. Iniciar Picking
  {
    target: '.start-picking-btn, [data-tour="start-picking"]',
    title: 'Iniciar Tarea de Picking',
    description: 'Haz clic en "Iniciar" para comenzar una tarea de picking. El sistema te guiará por la ruta óptima, ubicación por ubicación.',
  },

  // 6. Detalles de Picking
  {
    target: '.picking-items, [data-tour="picking-items"]',
    title: 'Items a Recolectar',
    description: 'Aquí ves todos los productos que debes recolectar, sus ubicaciones y cantidades. No verás precios ni costos: tu vista está enfocada solo en recoger lo correcto.',
  },

  // 7. Escanear y marcar item
  {
    target: '.complete-item-btn, [data-tour="complete-item"]',
    title: 'Escanear y Completar Item',
    description: 'Escanea el código del producto para confirmar que tomaste el correcto y márcalo como recolectado. Puedes ajustar la cantidad si encuentras una diferencia.',
  },

  // 8. Completar Picking + guía de despacho automática (NUEVO)
  {
    target: '.complete-picking-btn, [data-tour="complete-picking"]',
    title: 'Finalizar Picking (guía automática)',
    description: 'Cuando hayas recolectado todos los items, completa la tarea. El sistema genera automáticamente la GUÍA DE DESPACHO en PDF (con lote, vencimiento y cantidades, sin precios) y notifica al supervisor. ¡Eso cierra tu tarea!',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/operator') {
        router.push('/operator')
      }
    }
  },

  // 9. Consulta de Inventario
  {
    target: '[data-tour="inventory-link"]',
    title: 'Consultar Inventario',
    description: 'Navegaremos a la sección de Inventario para que puedas consultar ubicaciones y disponibilidad de productos cuando lo necesites.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/inventario') {
        router.push('/inventario')
      }
    }
  },

  // 10. Búsqueda de Productos
  {
    target: '.search-input, [data-tour="search-products"]',
    title: 'Buscar Productos',
    description: 'Usa la búsqueda para encontrar rápidamente productos por nombre, SKU o código de barras.',
  },

  // 11. Finalización
  {
    target: '.main-content',
    title: '¡Tour Completado!',
    description: '¡Perfecto! Ahora conoces todas las herramientas para realizar tus tareas de manera eficiente. ¡A trabajar!',
  }
]

export const viewerTour = [
  // 1. Bienvenida
  {
    target: '.main-content',
    title: 'Bienvenido Visualizador',
    description: 'Como visualizador, puedes consultar información y reportes del sistema sin realizar modificaciones.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/analytics') {
        router.push('/analytics')
      }
    }
  },

  // 2. Analytics
  {
    target: '[data-tour="analytics-link"]',
    title: 'Dashboard de Analytics',
    description: 'Visualiza métricas importantes, KPIs y el estado general de la operación: rotación ABC, bajo stock y punto de reorden.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/analytics') {
        router.push('/analytics')
      }
    }
  },

  // 3. Consulta de Órdenes
  {
    target: '[data-tour="orders-link"], [data-tour="sidebar"]',
    title: 'Consultar Órdenes',
    description: 'Navegaremos a la sección de Órdenes para que puedas ver el estado de todas las órdenes y su progreso.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/orders') {
        router.push('/orders')
      }
    }
  },

  // 4. Consulta de Inventario
  {
    target: '[data-tour="inventory-link"]',
    title: 'Consultar Inventario',
    description: 'Navegaremos a la sección de Inventario para que puedas revisar el stock disponible, ubicaciones y detalles de productos.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/inventario') {
        router.push('/inventario')
      }
    }
  },

  // 5. Trazabilidad
  {
    target: '[data-tour="trazabilidad-link"]',
    title: 'Trazabilidad',
    description: 'Navegaremos a la sección de Trazabilidad para que puedas consultar el historial de movimientos de cualquier producto.',
    action: (router) => {
      if (router && router.currentRoute.value.path !== '/trazabilidad') {
        router.push('/trazabilidad')
      }
    }
  },

  // 6. Finalización
  {
    target: '.main-content',
    title: 'Tour Completado',
    description: 'Ahora puedes navegar y consultar toda la información disponible en el sistema.',
  }
]

// ============================================================================
// TOUR DEL REVISOR GS1 (usuario `revisorgs1`)
// Recorre EN ORDEN los puntos del Checklist de Compatibilidad GS1, indicando en
// cada paso QUÉ punto se está demostrando y CÓMO probarlo. Los códigos de
// ejemplo son GS1 válidos (dígito verificador correcto) y se pueden reemplazar
// por los valores propios del evaluador.
// ============================================================================
export const gs1AuditTour = (() => {
  // Navegaciones reutilizables. Para el revisor: si la edición activa oculta
  // el módulo destino (ej. Egresos se oculta con "operarios"=sí), se ajustan
  // los ejes ANTES de navegar para que el guard no redirija y la demo GS1
  // sea visible completa.
  const go = (path) => async (router) => {
    if (!router) return
    if (path === '/egresos') {
      try {
        const store = (await import('@/store')).default
        if (!store.getters['edition/isModuleVisible']('egresos')) {
          store.commit('edition/setFlag', { key: 'pickingConOperarios', value: false })
        }
      } catch (e) { /* si el store no está disponible, navegar igual */ }
    }
    if (router.currentRoute.value.path !== path) router.push(path)
  }
  const INGRESOS = '[data-tour="ingresos-link"], [data-tour="sidebar"]'
  const INVENTARIO = '[data-tour="inventory-link"], [data-tour="sidebar"]'
  const EGRESOS = '[data-tour="egresos-link"], [data-tour="sidebar"]'
  const RECEPCIONES = '[data-tour="recepciones-link"], [data-tour="sidebar"]'
  const SIDEBAR = '[data-tour="sidebar"]'

  const INSTALACIONES = '[data-tour="installations-link"], [data-tour="sidebar"]'

  // Helper: paso de checklist (ID + observación de la 1ª evaluación + prueba + códigos)
  const punto = (id, tipo, texto, prueba, codes, target, path) => ({
    target,
    title: `Punto ${id} — ${tipo}`,
    checklistId: id,
    checklistType: tipo,
    checklistText: texto,
    prueba,
    codes: codes || [],
    action: path ? go(path) : undefined,
  })
  const FIX = 'Corregido'
  const OBS = 'Observación resuelta'

  return [
    {
      target: SIDEBAR,
      title: '2ª Evaluación GS1 — Correcciones de la 1ª revisión',
      description: 'Bienvenido nuevamente, revisor. Este recorrido cubre EXCLUSIVAMENTE los puntos observados en la primera evaluación (31/07/2026), en el orden del checklist. Cada paso muestra la OBSERVACIÓN original y, en la tarjeta "PRUEBA EN EL SISTEMA", cómo verificar la corrección con códigos listos para escanear o copiar (un clic selecciona el código completo). El recorrido navega solo a la pantalla de cada prueba y puede repetirse desde el botón de ayuda (?).',
    },

    // ═══ SECCIÓN 1 · CLAVES GS1 ═══
    punto('1.1 – 1.4', FIX,
      'OBSERVACIÓN: "Valida dígito verificador OK, pero admite letras. El AI (01) debe tener siempre 14 dígitos numéricos." (GTIN-13, GTIN-14, GTIN-12 y GTIN-8 — 4 puntos NO CUMPLE)',
      'El campo "Código de Barras / GTIN" ahora es SOLO NUMÉRICO: las letras se bloquean al teclear y se eliminan al pegar (intente pegar "ABC7800000000016XYZ" — quedan solo los dígitos). Largo obligatorio 8/12/13/14: pruebe el primer código (10 dígitos → rechazo por largo), el segundo (verificador malo → indica que el correcto es 6) y el tercero (válido → aceptado). La misma validación corre en el servidor (un POST directo con letras devuelve error 400). Los códigos internos alfanuméricos van en SKU o Código de Fábrica. En documentos e informes, el (01)/(02) se muestra SIEMPRE a 14 dígitos con ceros a la izquierda.',
      ['1234567890', '7800000000010', '7800000000016'], INVENTARIO, '/inventario'),
    punto('1.5', OBS,
      'OBSERVACIÓN: "Limitar hasta 20 caracteres" — el lote admitía ~30 en el input.',
      'Los campos Lote y N° de Serie de digitación manual (Ingresos y conteo de Recepciones) ahora tienen tope físico de 20 caracteres: al llegar a 20 el campo no acepta más, y si un valor excede muestra "No puede superar 20 caracteres". Pruebe tipear más de 20 caracteres en el campo Lote de Nuevo Ingreso. No se exige sintaxis de AI al digitar a mano: se escribe el valor directo.',
      [], INGRESOS, '/ingresos'),
    punto('1.10', OBS,
      'OBSERVACIÓN de la sesión: la serialización del SSCC debe ser CORRELATIVA ("si hiciste el 5, el siguiente es el 6"), un pallet no puede tener un solo elemento, y falta el peso como salida del SSCC.',
      'Tres correcciones: (1) CORRELATIVIDAD — los SSCC salen de una secuencia global atómica y quedan persistidos: genere documentos de dos egresos distintos y los seriales son consecutivos; re-descargar un documento REUTILIZA el mismo SSCC (no gasta correlativos). (2) MÍNIMO 2 — un egreso con 1 sola unidad NO emite SSCC: aparece el mensaje "un pallet SSCC requiere mínimo 2 elementos" y el documento sale como egreso unitario. (3) PESO — la etiqueta logística muestra peso neto/bruto y lo codifica como (3102) en el GS1-128 de contenido; el pallet MIXTO lleva solo SSCC + peso.',
      [], EGRESOS, '/egresos'),
    punto('1.16', OBS,
      'OBSERVACIÓN de la sesión: el GLN (414) se leía pero NO se podían crear ni gestionar GLN para las ubicaciones.',
      'En Instalaciones, cada BODEGA, ZONA y UBICACIÓN tiene ahora el botón GLN (icono de globo): asigna el siguiente GLN correlativo del prefijo de empresa (7801234...) y abre la etiqueta imprimible con el GLN como texto y su GS1-128 (414). Re-pulsar reimprime el MISMO GLN (no consume correlativos). Sin prefijo GS1 configurado el sistema lo impide con mensaje — un GLN solo puede construirse desde el prefijo licenciado; las ubicaciones mantienen además su código interno operativo (Code 128 no-GS1, rotulado como tal).',
      [], INSTALACIONES, '/instalaciones'),
    punto('1.19', OBS,
      'OBSERVACIÓN de la sesión: falta la funcionalidad de agrupar ≥2 SSCC bajo un GSIN (402) con su packing list múltiple.',
      'En Egresos: columna "Agrupar" con casillas — seleccione 2 o más egresos y pulse "PACKING LIST MÚLTIPLE (GSIN)". El sistema emite el GSIN correlativo (o reutiliza el del grupo), y el PDF lista cada pallet con su SSCC como texto + su detalle, los totales del envío y el GSIN (402) IMPRESO AL FINAL con la leyenda "no se adhiere a ningún bulto físico: acompaña esta documentación" — el SSCC se pega en el pallet, el GSIN viaja en los papeles. Con menos de 2 seleccionados el sistema lo rechaza con mensaje.',
      [], EGRESOS, '/egresos'),
    punto('1.20', FIX,
      'OBSERVACIÓN: "Definir la longitud del dato (hasta 30 caracteres)" — un AI 90-99 aceptó 31 caracteres.',
      'El límite de 30 ahora se VALIDA: abra el Validador GS1 y pegue el primer código — (91) con 31 caracteres — y verá "31 caracteres supera el máximo de 30"; el segundo, con 30 exactos, es válido y se rotula "Uso interno 91 (hasta 30)". Además, al imprimir la etiqueta logística de un egreso puede INCLUIR un AI 90-99 opcional (diálogo previo: AI + valor máx. 30) que se codifica en el GS1-128 de contenido y aparece como texto.',
      ['(91)' + 'A'.repeat(31), '(91)' + 'A'.repeat(30)], INGRESOS, '/ingresos'),

    // ═══ SECCIÓN 2 · SIMBOLOGÍAS ═══
    punto('2.3', FIX,
      'OBSERVACIÓN: "Lee GS1-128 pero no lo crea bien: le coloca AUTO (delante) en el egreso" — el SKU autogenerado anteponía la palabra AUTO y truncaba el GTIN.',
      'Corregido en código Y en datos: al crear un producto desde un código GS1-128 escaneado, el SKU propuesto es el GTIN COMPLETO, sin prefijos ni truncado (escanee el código adjunto — GTIN no registrado — y revise el campo SKU del modal de creación). Los productos antiguos que quedaron con "AUTO..." fueron corregidos: en el egreso y sus documentos hoy se ve el GTIN limpio como identificación primaria.',
      ['0107801234560007172701181090LOTEB'], INGRESOS, '/ingresos'),
    punto('2.5', FIX,
      'OBSERVACIÓN: "Lee pero aparece como expandido solo y no muestra FNC1 en la lectura" (GS1 DataBar).',
      'Dos correcciones: (1) la simbología de impresión ahora se llama y genera "GS1 DataBar EXPANDIDO APILADO" (única opción DataBar del modal de impresión — motor BWIPP). (2) La lectura MUESTRA el separador: pegue el código adjunto (DataBar con AIM ]e0, serie seguida de vencimiento) en el Validador y verá el chip "Separadores FNC1 detectados", la secuencia con ⟨FNC1·GS⟩ marcado tras el AI variable, y la columna FNC1 en el desglose.',
      [']e0010780005000308121SERIE1002917270118'], INGRESOS, '/ingresos'),
    punto('2.8', OBS,
      'OBSERVACIÓN de la sesión: el UPC-E requiere la librería completa de validación (4 formas de expansión según el 7° dígito, verificador derivado del UPC-A) y bloquear la digitación inválida.',
      'La validación usa la expansión completa: escriba el primer código en el campo GTIN de Crear Producto — es un UPC-E puro (NO valida como EAN-8) y el sistema lo acepta mostrando "UPC-E válido → UPC-A 012200003453". El segundo (comienza con 9, imposible para UPC-E) se rechaza explicando la regla. Las 4 formas de expansión están cubiertas con batería de pruebas automatizada — incluido el caso ambiguo de un código válido como EAN-8 y UPC-E a la vez, que solo el AIM del lector puede desambiguar.',
      ['01234523', '91234565'], INVENTARIO, '/inventario'),

    // ═══ SECCIÓN 3 · INDICADORES DE APLICACIÓN ═══
    punto('3.2', FIX,
      'OBSERVACIÓN: "El GS1-128 se genera pero al leerlo aparece como CODE 128."',
      'Verificado a nivel de codificación: el GS1-128 generado SÍ lleva el FNC1 en primera posición (medido contra BWIPP: exactamente 1 codeword más que un Code 128 plano con el mismo dato). La prueba definitiva es el identificador AIM del lector: pegue el primer código en el Validador — llega con ]C1, que SOLO existe si hay FNC1 inicial — y el sistema lo rotula "GS1-128"; el segundo llega con ]C0 y se rotula "Code 128 (NO GS1)" en rojo. Nota del propio validador: los motores de escaneo móviles (ZXing/ML Kit) rotulan "CODE_128" a la FAMILIA física — el ]C1 y los separadores GS confirman que es GS1-128 legítimo.',
      [']C101078000500030811727011810LOTEA', ']C0REF-INTERNA-99'], INGRESOS, '/ingresos'),
    punto('3.4', FIX,
      'OBSERVACIÓN: "En lectura del GS1 DataBar no coloca el FNC1."',
      'El FNC1 de separación ahora se gestiona internamente Y se muestra: en el Validador, la "Secuencia interpretada" marca ⟨FNC1·GS⟩ justo después de cada AI variable que venía separado (el FNC1 se transmite como el carácter GS, ASCII 29 — el mismo "GS" que muestra su aplicación de verificación). La tabla de desglose indica por cada AI si hubo FNC1, si terminó el código o si el AI es de largo fijo (donde el estándar prohíbe FNC1). Funciona con el carácter GS real, con el texto "0029" de los lectores Zebra y en las simbologías DataBar, DataMatrix, QR y GS1-128.',
      ['010780005000308121SERIE1002917270118'], INGRESOS, '/ingresos'),
    punto('3.6', OBS,
      'OBSERVACIÓN: "Falta generar etiquetas y enviarlas a GS1 Chile para validación."',
      'Las muestras físicas se entregan por separado, impresas en etiqueta autoadhesiva con impresora de transferencia térmica: set completo de EAN-13, EAN-8, UPC-A, UPC-E, ITF-14, GS1-128, GS1 DataMatrix, GS1 DataBar Expandido Apilado, GS1 QR y la etiqueta logística SSCC. Pre-verificación en el sistema: BWIPP valida estructura y verificador de cada símbolo AL GENERARLO (un código malformado no se imprime), y el Validador GS1 permite re-leer cualquier muestra antes de enviarla.',
      [], INGRESOS, '/ingresos'),

    // ═══ SECCIÓN 4 · REPORTES ═══
    punto('4.1', FIX,
      'OBSERVACIÓN: "Se requieren reportes de entrada y salida que contengan las claves GS1 (GTIN, lote, fechas, SSCC, cantidad, origen y destino)."',
      'Nuevo reporte de MOVIMIENTOS GS1: en Egresos el botón "EXCEL GS1" exporta las SALIDAS y en Ingresos el mismo botón exporta las ENTRADAS — Excel real con 14 columnas etiquetadas por AI: Movimiento, Fecha, Documento, ORIGEN (proveedor/bodega), DESTINO (ubicación/cliente), SSCC (00), GSIN (402), GTIN-14 (01), Descripción, Lote (10), Venc. (17), Prod. (11), Serie (21) y Cantidad (37). El Informe de Jerarquía muestra además los 3 niveles con (01)/(02) SIEMPRE a 14 dígitos y la nomenclatura corregida: unidad comercial (01) · unidad de despacho (01) · unidad logística (02)+(37).',
      [], EGRESOS, '/egresos'),
    punto('4.2', FIX,
      'OBSERVACIÓN: "Corregir ambos Packing List para cumplir el punto. Las cajas con (02) y debería ser (01)."',
      'Semántica corregida en todos los documentos: las CAJAS (unidad de despacho) y las unidades comerciales se identifican con (01); el (02)+(37) queda exclusivamente donde corresponde — declarando el CONTENIDO de la unidad logística (nivel 1 del informe de jerarquía y GS1-128 de la etiqueta del pallet). Descargue el Informe de Jerarquía de un egreso y verifique los niveles; el packing list identifica por GTIN-14/GTIN-13 y el envío por su SSCC, con el GTIN como identificación primaria (sin códigos internos).',
      [], EGRESOS, '/egresos'),
    punto('4.3', FIX,
      'OBSERVACIÓN: "La capacidad de filtrado por claves debe ser ágil y completa (entrada y salida)."',
      'Filtro ágil en Egresos: un solo campo "Clave GS1" donde escanea o escribe CUALQUIER clave y el sistema detecta el tipo por su estructura (18 dígitos=SSCC, 17=GSIN, 8-14=GTIN, resto=lote) — pruebe con el SSCC adjunto y la tabla se reduce a ese envío. El chip superior responde la pregunta de la sesión: "Último SSCC enviado: ... → cliente". Los exports Excel GS1 de entrada y salida respetan los filtros activos. Inventario y Trazabilidad mantienen sus filtros por GTIN/lote/serie/SSCC/fechas.',
      ['178012340000000015'], EGRESOS, '/egresos'),
    punto('4.4', FIX,
      'OBSERVACIÓN: "Los Packing List deben cumplir con el punto obligatorio."',
      'El PACKING LIST DE DESPACHO (SALIDA) contiene todas las claves exigidas: SSCC (00) como identificación primaria, GSIN (402) si el envío está agrupado, GTIN-14, GTIN-13, Lote (10), Fechas (17), Cantidades (37), descripciones detalladas y peso del pallet — en PDF imprimible y JSON electrónico (base DESADV). El packing list MÚLTIPLE agrega el detalle por pallet (SSCC visible como texto) y el GSIN impreso al final. Descargue ambos desde los botones de un egreso.',
      [], EGRESOS, '/egresos'),
    punto('4.5', FIX,
      'OBSERVACIÓN: "Falta mostrar Packing List con claves GS1 que entran y salen de la empresa sin digitación manual."',
      'Documentos separados y visibles en ambos sentidos: (1) SALIDA — el egreso genera su "PACKING LIST DE DESPACHO (SALIDA)". (2) ENTRADA — importe ese mismo JSON en Recepciones → Nueva Recepción → "Importar Packing List (JSON)": la recepción esperada se crea sola mapeando por GTIN, CERO digitación; y el nuevo botón de packing list en cada recepción muestra el "PACKING LIST DE RECEPCIÓN (ENTRADA)" con el SSCC de origen, el proveedor y las claves de cada línea. El ciclo circular completo (sale → entra) queda demostrado con documentos distintos para cada dirección.',
      [], RECEPCIONES, '/recepciones'),

    {
      target: SIDEBAR,
      title: 'Fin del recorrido — correcciones de la 1ª evaluación',
      description: 'Recorrido completo de los puntos observados el 31/07/2026: Sección 1 (1.1-1.4 GTIN numérico, 1.5 lote 20, 1.10 SSCC correlativo/mínimo 2/peso, 1.16 GLN, 1.19 GSIN, 1.20 AIs 90-99), Sección 2 (2.3 SKU-GTIN, 2.5 DataBar Expandido Apilado, 2.8 UPC-E), Sección 3 (3.2 GS1-128/]C1, 3.4 FNC1 visible, 3.6 muestras), Sección 4 (4.1 reportes con claves y origen/destino, 4.2 semántica 01/02, 4.3 filtros ágiles, 4.4 packing list completo, 4.5 entrada/salida sin digitación). Puede repetir el recorrido desde el botón (?) y probar cada punto con sus propios códigos.',
    },
  ]
})()

// Función para obtener el tour según el rol del usuario.
// Incluye los 5 roles granulares nuevos + los roles legacy (admin/operator/viewer).
// `username` permite tours por usuario específico (ej. el revisor de GS1).
export function getTourByRole(userRole, username, sectorSalud) {
  if (username && String(username).toLowerCase() === 'revisorgs1') {
    return gs1AuditTour
  }
  // Institución de salud: tutorial de uso por perfil (NT 226 / NGT 247)
  if (sectorSalud) {
    return getSaludTour(userRole)
  }
  switch (userRole) {
    // Gestión completa de la bodega → recorrido de puesta en marcha
    case 'admin':
    case 'warehouse_manager':
      return supervisorTour
    // Operación de piso (recolección / recepción) → recorrido del operario
    case 'operator':
    case 'picker':
    case 'receiving_operator':
      return operatorTour
    // Solo consulta / ventas → recorrido de visualización
    case 'salesperson':
    case 'viewer':
      return viewerTour
    default:
      return supervisorTour
  }
}

// Función para verificar si el usuario ya completó el tour
export function hasCompletedTour(userRole) {
  return localStorage.getItem(`tour-completed-${userRole}`) === 'true'
}

// Función para marcar el tour como completado
export function markTourAsCompleted(userRole) {
  localStorage.setItem(`tour-completed-${userRole}`, 'true')
}

// Función para resetear el tour (útil para testing)
export function resetTour(userRole) {
  localStorage.removeItem(`tour-completed-${userRole}`)
}
